import React, { useEffect, useMemo, useState } from "react";
import api from "../utils/axiosConfig";
import localforage from "localforage";
import Dashboard from "./Dashboard";

const API_URL = process.env.REACT_APP_API_URL;

const INK = "#1e2a3a";
const MUTED = "#6b7a8d";
const LINE = "#e6ecf3";
const BLUE = "#2563eb";
const INDIGO = "#5b5bd6";
const GREEN = "#12a150";
const AMBER = "#e5a13a";
const RED = "#e5484d";
const BG = "#f7f9fc";

const SEV = { High: RED, Medium: AMBER, Low: GREEN };
const CAT_COLOR = {
  "Caste Census & Booth Committees": BLUE,
  "Welfare Schemes & Beneficiaries": GREEN,
  "Infrastructure & Civic Issues": "#0e7fc0",
  "Party Organisation & Cadre": INDIGO,
  "Leadership & Coordination": RED,
  "Opposition & Political Dynamics": "#a855f7",
  "Employment & Livelihood": AMBER,
  "Law & Order / Grievances": "#64748b",
  "Other Local Matters": "#7a8794",
};

const card = { background: "#fff", border: `1px solid ${LINE}`, borderRadius: 14, padding: 18 };
const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

const MomMetaData = () => {
  const [geo, setGeo] = useState({});
  const [zone, setZone] = useState("");
  const [pc, setPc] = useState("");
  const [ac, setAc] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState({}); // category -> details expanded

  const authHeader = async () => {
    const token = await localforage.getItem("token");
    return { Authorization: "Bearer " + token };
  };

  useEffect(() => {
    (async () => {
      try {
        const headers = await authHeader();
        const res = await api.get(`${API_URL}/mom-format/geo-hierarchy`, { headers });
        setGeo(res.data || {});
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const zones = useMemo(() => Object.keys(geo).sort(), [geo]);
  const pcs = useMemo(() => (zone && geo[zone] ? Object.keys(geo[zone]).sort() : []), [geo, zone]);
  const acs = useMemo(
    () => (zone && pc && geo[zone] && geo[zone][pc] ? geo[zone][pc] : []), [geo, zone, pc]);

  const getData = async () => {
    setLoading(true); setErr(""); setData(null); setOpen({});
    try {
      const headers = await authHeader();
      const params = {};
      if (zone) params.zone = zone;
      if (pc) params.pc = pc;
      if (ac) params.ac = ac;
      if (from) params.from = from;
      if (to) params.to = to;
      const res = await api.get(`${API_URL}/mom-format/meta-summary`, { headers, params, timeout: 180000 });
      setData(res.data);
    } catch (e) {
      setErr(e.response?.data?.error || "Could not load the summary.");
    } finally { setLoading(false); }
  };

  const reset = () => { setZone(""); setPc(""); setAc(""); setFrom(""); setTo(""); setData(null); setErr(""); };

  const downloadCSV = () => {
    if (!data) return;
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
    };
    const rows = [["Category", "Severity", "Summary", "Key Issues", "Recommended Actions", "Points", "PCMs"]];
    data.categories.forEach((c) => rows.push([
      c.category, c.severity || "", c.aiSummary || c.narrative,
      (c.keyIssues || []).join("; "), (c.recommendedActions || []).join("; "),
      c.count, c.byPcm.map((p) => `${p.pcm} (${p.count})`).join("; "),
    ]));
    const blob = new Blob([rows.map((r) => r.map(esc).join(",")).join("\n")],
      { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "mom-meta-summary.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const sel = { padding: "9px 11px", border: `1px solid ${LINE}`, borderRadius: 10,
                fontSize: 13, background: "#fff", color: INK, width: "100%" };
  const lbl = { fontSize: 11.5, fontWeight: 700, color: MUTED, display: "block", marginBottom: 4 };

  return (
    <div>
      <Dashboard />
      <div style={{ background: BG, minHeight: "100vh", padding: "18px 24px" }}>
        <div style={{ width: "100%", maxWidth: "none", margin: 0 }}>

          <div style={{ ...card, marginBottom: 14 }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: INK }}>MoM Meta Data</div>
            <div style={{ fontSize: 13, color: MUTED, marginBottom: 14 }}>
              AI-summarised meeting intelligence by issue category
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
              <div>
                <label style={lbl}>ZONE</label>
                <select value={zone} onChange={(e) => { setZone(e.target.value); setPc(""); setAc(""); }} style={sel}>
                  <option value="">All Zones</option>
                  {zones.map((z) => <option key={z} value={z}>{z}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>PARLIAMENT CONSTITUENCY</label>
                <select value={pc} onChange={(e) => { setPc(e.target.value); setAc(""); }} style={sel} disabled={!zone}>
                  <option value="">All PCs</option>
                  {pcs.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>ASSEMBLY CONSTITUENCY</label>
                <select value={ac} onChange={(e) => setAc(e.target.value)} style={sel} disabled={!pc}>
                  <option value="">All ACs</option>
                  {acs.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>FROM DATE</label>
                <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={sel} />
              </div>
              <div>
                <label style={lbl}>TO DATE</label>
                <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={sel} />
              </div>
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
              <button type="button" onClick={getData} disabled={loading}
                style={{ background: BLUE, color: "#fff", border: "none", borderRadius: 10,
                         padding: "10px 22px", fontWeight: 700, cursor: "pointer", fontSize: 13.5 }}>
                {loading ? "Analysing…" : "Get Data"}
              </button>
              {data && (
                <button type="button" onClick={downloadCSV}
                  style={{ background: GREEN, color: "#fff", border: "none", borderRadius: 10,
                           padding: "10px 20px", fontWeight: 700, cursor: "pointer", fontSize: 13.5 }}>
                  Download CSV
                </button>
              )}
              <button type="button" onClick={reset}
                style={{ background: "#eef2f7", color: INK, border: `1px solid ${LINE}`, borderRadius: 10,
                         padding: "10px 18px", fontWeight: 600, cursor: "pointer", fontSize: 13.5 }}>
                Reset
              </button>
            </div>
          </div>

          {err && <p style={{ color: RED, textAlign: "center", fontWeight: 600 }}>{err}</p>}
          {loading && (
            <p style={{ textAlign: "center", color: MUTED }}>
              Reading meeting records and generating the AI summary… this can take up to a minute.
            </p>
          )}

          {data && !loading && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 14 }}>
                {[["Meetings", data.totals.meetings], ["Points", data.totals.points],
                  ["Categories", data.totals.categories], ["Actionables", data.totals.actionables],
                  ["PCMs", data.totals.pcms]].map(([t, v]) => (
                  <div key={t} style={{ ...card, padding: 14 }}>
                    <div style={{ fontSize: 11, color: MUTED, fontWeight: 700 }}>{t.toUpperCase()}</div>
                    <div style={{ fontSize: 26, fontWeight: 800, color: INK }}>{fmt(v)}</div>
                  </div>
                ))}
              </div>

              {data.executiveSummary && (
                <div style={{ ...card, marginBottom: 14, borderLeft: `5px solid ${INDIGO}` }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: INDIGO, letterSpacing: ".6px", marginBottom: 8 }}>
                    EXECUTIVE SUMMARY
                  </div>
                  <p style={{ fontSize: 14.5, color: "#2b3646", lineHeight: 1.7, margin: 0 }}>
                    {data.executiveSummary}
                  </p>
                </div>
              )}

              {data.ai && data.ai.enabled && !data.ai.ok && (
                <div style={{ ...card, marginBottom: 14, borderLeft: `5px solid ${AMBER}`, fontSize: 13, color: MUTED }}>
                  The AI service did not respond, so the statistical summary is shown instead.
                  Check the API key and quota on the server.
                </div>
              )}
              {data.ai && !data.ai.enabled && (
                <div style={{ ...card, marginBottom: 14, borderLeft: `5px solid ${AMBER}`, fontSize: 13, color: MUTED }}>
                  AI summarisation is not configured — showing the statistical summary.
                  Add GROQ_API_KEY (or GEMINI_API_KEY) to the backend .env and restart.
                </div>
              )}

              {data.totals.meetings === 0 && (
                <div style={{ ...card, textAlign: "center", color: MUTED }}>
                  No meeting records match this selection.
                </div>
              )}

              {data.categories.map((c) => {
                const color = CAT_COLOR[c.category] || "#7a8794";
                const sev = SEV[c.severity] || MUTED;
                const isOpen = !!open[c.category];
                return (
                  <div key={c.category} style={{ ...card, borderLeft: `5px solid ${color}`, marginBottom: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                      <div style={{ fontSize: 16, fontWeight: 800, color: INK }}>{c.category}</div>
                      <div style={{ display: "flex", gap: 7, alignItems: "center" }}>
                        {c.severity && (
                          <span style={{ background: `${sev}18`, color: sev, borderRadius: 7,
                                         padding: "3px 10px", fontSize: 11, fontWeight: 800 }}>
                            {c.severity.toUpperCase()}
                          </span>
                        )}
                        <span style={{ background: `${color}15`, color, borderRadius: 7,
                                       padding: "3px 10px", fontSize: 11.5, fontWeight: 700 }}>
                          {c.count} point{c.count === 1 ? "" : "s"}
                        </span>
                      </div>
                    </div>

                    <p style={{ fontSize: 14, color: "#2b3646", lineHeight: 1.65, margin: "10px 0 0" }}>
                      {c.aiSummary || c.narrative}
                    </p>

                    {(c.keyIssues || []).length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 10.5, fontWeight: 800, color: MUTED, letterSpacing: ".5px", marginBottom: 6 }}>
                          KEY ISSUES
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, color: "#374151", lineHeight: 1.75 }}>
                          {c.keyIssues.map((k, i) => <li key={i}>{k}</li>)}
                        </ul>
                      </div>
                    )}

                    {(c.recommendedActions || []).length > 0 && (
                      <div style={{ marginTop: 12, background: "#f7faff", border: "1px solid #e2ecfb",
                                    borderRadius: 10, padding: "10px 14px" }}>
                        <div style={{ fontSize: 10.5, fontWeight: 800, color: BLUE, letterSpacing: ".5px", marginBottom: 6 }}>
                          RECOMMENDED ACTIONS
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, color: "#1e3a5f", lineHeight: 1.75 }}>
                          {c.recommendedActions.map((a, i) => <li key={i}>{a}</li>)}
                        </ul>
                      </div>
                    )}

                    <div style={{ marginTop: 12, borderTop: `1px solid ${LINE}`, paddingTop: 9,
                                  display: "flex", justifyContent: "space-between", alignItems: "center",
                                  flexWrap: "wrap", gap: 8 }}>
                      <div style={{ fontSize: 12, color: MUTED }}>
                        Recorded by:{" "}
                        {c.byPcm.slice(0, 4).map((p, i) => (
                          <span key={p.pcm}>
                            <strong style={{ color: INK }}>{p.pcm}</strong> ({p.count})
                            {i < Math.min(c.byPcm.length, 4) - 1 ? ", " : ""}
                          </span>
                        ))}
                        {c.byPcm.length > 4 ? ` +${c.byPcm.length - 4} more` : ""}
                      </div>
                      <button type="button"
                        onClick={() => setOpen((o) => ({ ...o, [c.category]: !isOpen }))}
                        style={{ background: "none", border: "none", color: BLUE, fontWeight: 700,
                                 cursor: "pointer", fontSize: 12.5 }}>
                        {isOpen ? "Hide source points ▲" : "View source points ▼"}
                      </button>
                    </div>

                    {isOpen && (
                      <div style={{ marginTop: 10, borderTop: `1px dashed ${LINE}`, paddingTop: 10 }}>
                        {c.highlights.map((h, i) => (
                          <div key={i} style={{ padding: "7px 0", borderBottom: `1px solid ${LINE}` }}>
                            <div style={{ fontSize: 13, color: INK, lineHeight: 1.55 }}>{h.text}</div>
                            <div style={{ fontSize: 11.5, color: MUTED, marginTop: 3 }}>
                              <strong style={{ color: INDIGO }}>{h.pcm}</strong>
                              {h.ac ? ` · ${h.ac}` : ""}{h.date ? ` · ${h.date}` : ""}
                              {h.respondent ? ` · met ${h.respondent}${h.designation ? ` (${h.designation})` : ""}` : ""}
                            </div>
                          </div>
                        ))}
                        {c.actionables.length > 0 && (
                          <div style={{ marginTop: 10 }}>
                            <div style={{ fontSize: 10.5, fontWeight: 800, color: MUTED, marginBottom: 5 }}>
                              ACTIONABLES RECORDED
                            </div>
                            {c.actionables.map((a, i) => (
                              <div key={i} style={{ fontSize: 12.5, color: "#374151", marginBottom: 5, lineHeight: 1.5 }}>
                                • <strong>{a.issue}</strong> → {a.actionable}
                                {a.timeline ? <span style={{ color: MUTED }}> ({a.timeline})</span> : null}
                                <span style={{ color: INDIGO }}> · {a.pcm}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {data.pcmTotals.length > 0 && (
                <div style={card}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: INK, marginBottom: 10 }}>
                    PCM Reporting Summary
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: "#f2f5fa" }}>
                        <th style={{ padding: "8px 10px", textAlign: "left", border: `1px solid ${LINE}` }}>PCM</th>
                        <th style={{ padding: "8px 10px", textAlign: "center", border: `1px solid ${LINE}` }}>Meetings</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.pcmTotals.map((p) => (
                        <tr key={p.pcm}>
                          <td style={{ padding: "7px 10px", border: `1px solid ${LINE}` }}>{p.pcm}</td>
                          <td style={{ padding: "7px 10px", border: `1px solid ${LINE}`, textAlign: "center", fontWeight: 700 }}>
                            {p.meetings}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MomMetaData;
